import { useQueryParams } from "@evg-ui/lib/hooks";
import { toSentenceCase } from "@evg-ui/lib/utils/string";
import { array } from "utils";
import { FilterChipType } from "./FilterChip";

const { convertObjectToArray } = array;

/**
 * useFilterChipQueryParams is used alongside the FilterChips component to tie its state to query params
 * @param validQueryParams - a set of valid query params that the FilterChips component can use
 * @param urlParamToTitleMap - a map of the url param to the title that should be shown in the chip
 * @returns - an object with the following properties:
 * `queryParamsList` - a list of chips that are currently in the query params
 * `handleClearAll` - a function that clears all chips from the query params
 * `handleOnRemove` - a function that removes a chip from the query params
 */
const useFilterChipQueryParams = (
  validQueryParams: Set<string>,
  urlParamToTitleMap?: { [urlParam: string]: string },
) => {
  const [queryParams, setQueryParams] = useQueryParams();

  const queryParamsList = convertObjectToArray(queryParams).filter(({ key }) =>
    validQueryParams.has(key as string),
  );

  const chips: FilterChipType[] = queryParamsList.map((q) => ({
    key: q.key,
    value: q.value as string,
    title: urlParamToTitleMap?.[q.key] ?? toSentenceCase(q.key),
  }));

  const handleClearAll = () => {
    setQueryParams((current) => {
      const params: Record<string, unknown> = { ...current };
      Object.keys(params)
        .filter((chip) => validQueryParams.has(chip))
        .forEach((v) => {
          params[v] = undefined;
        });
      return params;
    });
  };
  const handleOnRemove = (chip: FilterChipType) => {
    setQueryParams((current) => {
      const currentValue = current[chip.key] as string | string[] | undefined;
      const updatedParam = popQueryParams(currentValue ?? [], chip.value);
      return { ...current, [chip.key]: updatedParam };
    });
  };

  return {
    chips,
    handleClearAll,
    handleOnRemove,
  };
};

const popQueryParams = (param: string | string[], value: string) => {
  if (Array.isArray(param)) {
    return param.filter((p) => p !== value);
  }
  return undefined;
};

export default useFilterChipQueryParams;
